import { DateTime } from "luxon";
import { AvailabilityMonthModel, AvailabilityTimeModel, IdNameItemModel, SlotType } from "types/generated/models";
import { ITimeEntry } from "types/appointmentBooking";

export interface IDay {
    date: DateTime | null;
    availability: {
        morning: ISlot[] | boolean;
        afternoon: ISlot[] | boolean;
        evening: ISlot[] | boolean;
    } | null;
}

export interface ISlot {
    id: string;
    date: DateTime;
    hour: string;
    dateTimeOffset: string;
    start: string;
    duration: number;
    name?: string | null;
    description?: string | null;
    appointmentId?: number | null;
    providerId?: number | null;
    providers?: IdNameItemModel[] | null;
    locationId?: number | null;
    type: SlotType;
    isBooked: boolean;
    originalTimeEntry: AvailabilityTimeModel;
}

export interface IDayEntry {
    date: string;
    number: number;
    timeEntries: ITimeEntry[];
}

export interface IDayPeriod {
    morning: boolean;
    afternoon: boolean;
    evening: boolean;
}

export function slotsForSelectedDay(day: IDay) {
    const availability = day.availability;
    if (!availability) return {};

    return {
        morningSlots: Array.isArray(availability.morning) ? availability.morning : null,
        afternoonSlots: Array.isArray(availability.afternoon) ? availability.afternoon : null,
        eveningSlots: Array.isArray(availability.evening) ? availability.evening : null,
    };
}

export function convertTimeEntriesToSlots(timeEntries: ITimeEntry[]): ISlot[] {
    return timeEntries.map((entry, index) => ({
        id: `${entry.start}-${index}`,
        date: DateTime.fromISO(entry.dateTimeOffset),
        hour: entry.start,
        dateTimeOffset: entry.dateTimeOffset,
        start: entry.start,
        duration: entry.duration,
        name: entry.name,
        description: entry.description,
        appointmentId: entry.appointmentId,
        providerId: entry.providerId,
        providers: entry.providers,
        locationId: entry.locationId,
        type: entry.type,
        isBooked: entry.isBooked,
        originalTimeEntry: {
            dateTimeOffset: entry.dateTimeOffset,
            start: entry.start,
            duration: entry.duration,
            name: entry.name,
            description: entry.description,
            appointmentId: entry.appointmentId,
            providerId: entry.providerId,
            providers: entry.providers,
            locationId: entry.locationId,
            type: entry.type,
            isBooked: entry.isBooked,
        },
    }));
}

export function availabilityRangeLimits() {
    return {
        morning: [0, 12],
        afternoon: [12, 18],
        evening: [18, 24],
    };
}

export function getAvailabilityRange(dayEntry: IDayEntry | undefined): IDayPeriod {
    if (!dayEntry) {
        return { morning: false, afternoon: false, evening: false };
    }

    const limits = availabilityRangeLimits();
    const slots = convertTimeEntriesToSlots(dayEntry.timeEntries);

    return {
        morning: slots.some((slot) => {
            const hour = slot.date.hour;
            return hour >= limits.morning[0] && hour < limits.morning[1];
        }),
        afternoon: slots.some((slot) => {
            const hour = slot.date.hour;
            return hour >= limits.afternoon[0] && hour < limits.afternoon[1];
        }),
        evening: slots.some((slot) => {
            const hour = slot.date.hour;
            return hour >= limits.evening[0] && hour < limits.evening[1];
        }),
    };
}

export function processAvailability(dayEntry: IDayEntry | undefined, range: IDayPeriod) {
    if (!dayEntry) {
        return null;
    }

    const slots = convertTimeEntriesToSlots(dayEntry.timeEntries);
    const limits = availabilityRangeLimits();

    return {
        morning: range.morning
            ? slots.filter((slot) => {
                  const hour = slot.date.hour;
                  return hour >= limits.morning[0] && hour < limits.morning[1];
              })
            : false,
        afternoon: range.afternoon
            ? slots.filter((slot) => {
                  const hour = slot.date.hour;
                  return hour >= limits.afternoon[0] && hour < limits.afternoon[1];
              })
            : false,
        evening: range.evening
            ? slots.filter((slot) => {
                  const hour = slot.date.hour;
                  return hour >= limits.evening[0] && hour < limits.evening[1];
              })
            : false,
    };
}

export function getMonthDates(month: DateTime): IDay[] {
    const daysInMonth = month.daysInMonth;
    const days: IDay[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
        days.push({
            date: month.set({ day }),
            availability: null,
        });
    }

    return days;
}

export function getMonthWeeks(dates: IDay[]): IDay[][] {
    const firstDayOfMonth = dates[0].date;
    const lastDayOfMonth = dates.at(-1)?.date;

    if (!firstDayOfMonth || !lastDayOfMonth) return [];

    const beforeDays = firstDayOfMonth.weekday === 7 ? 6 : firstDayOfMonth.weekday - 1;
    const afterDays = lastDayOfMonth.weekday === 7 ? 0 : 7 - lastDayOfMonth.weekday;

    for (let i = 0; i < beforeDays; i++) {
        dates.unshift({ date: null, availability: null });
    }

    for (let i = 0; i < afterDays; i++) {
        dates.push({ date: null, availability: null });
    }

    const weeks: IDay[][] = [];
    for (let i = 0; i < dates.length; i += 7) {
        weeks.push(dates.slice(i, i + 7));
    }

    return weeks;
}

function createTimeEntry(timeEntry: AvailabilityTimeModel): ITimeEntry {
    return {
        dateTimeOffset: timeEntry.dateTimeOffset || "",
        start: timeEntry.start || "",
        duration: timeEntry.duration || 0,
        name: timeEntry.name || null,
        description: timeEntry.description || null,
        appointmentId: timeEntry.appointmentId || null,
        providerId: timeEntry.providerId || null,
        providers: timeEntry.providers || null,
        locationId: timeEntry.locationId || null,
        type: timeEntry.type || SlotType.Available,
        isBooked: timeEntry.isBooked || false,
    };
}

function processTimeEntry(
    timeEntry: AvailabilityTimeModel,
    timeEntriesByDate: { [key: string]: ITimeEntry[] },
    requestedMonth: number,
    minutesAhead: number
): void {
    if (!timeEntry.dateTimeOffset) return;

    const entryDate = DateTime.fromISO(timeEntry.dateTimeOffset);
    const localMonth = entryDate.month;

    const currentTime = DateTime.now().plus({ minutes: minutesAhead });
    const isInThePast = entryDate < currentTime;

    if (requestedMonth !== localMonth || isInThePast) {
        return;
    }

    const dateKey = entryDate.toISODate();

    if (!timeEntriesByDate[dateKey]) {
        timeEntriesByDate[dateKey] = [];
    }

    timeEntriesByDate[dateKey].push(createTimeEntry(timeEntry));
}

function processDayEntries(data: AvailabilityMonthModel): { [key: string]: ITimeEntry[] } {
    const timeEntriesByDate: { [key: string]: ITimeEntry[] } = {};

    if (!data.dayEntries) return timeEntriesByDate;

    const requestedMonth = data.month || DateTime.now().month;
    const minutesAhead = data.minutesAhead || 0;

    for (const dayEntry of data.dayEntries) {
        if (!dayEntry.timeEntries || !dayEntry.date) continue;

        for (const timeEntry of dayEntry.timeEntries) {
            processTimeEntry(timeEntry, timeEntriesByDate, requestedMonth, minutesAhead);
        }
    }

    return timeEntriesByDate;
}

function applyAvailabilityToDay(day: IDay, timeEntriesByDate: { [key: string]: ITimeEntry[] }): void {
    if (!day.date) return;

    const dateKey = day.date.toISODate();
    const timeEntries = timeEntriesByDate[dateKey];

    if (!timeEntries) return;

    const dayEntry = {
        date: day.date.toISO(),
        number: day.date.day,
        timeEntries,
    };
    const range = getAvailabilityRange(dayEntry);
    day.availability = processAvailability(dayEntry, range);
}

export function getAvailability(date: DateTime, data: AvailabilityMonthModel): IDay[][] {
    const timeEntriesByDate = processDayEntries(data);
    const days = getMonthDates(date);

    for (const day of days) {
        applyAvailabilityToDay(day, timeEntriesByDate);
    }

    return getMonthWeeks(days);
}
